import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CheckCircle as ApproveIcon, XCircle as RejectIcon, RefreshCw as RefreshIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ShellHeader, Textarea } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import {
  hitlService,
  TaskStatus,
  type ExternalTaskDetailDto,
  type UpdateTaskStatusRequest,
} from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';

/* StatusColor helper — currently unused after migration to @coordio/ui StatusBadge.
   Kept as reference for the MUI→StatusColor mapping. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function statusColor(status: TaskStatus) {
  switch (status) {
    case TaskStatus.Pending:
      return 'warning' as const;
    case TaskStatus.InProgress:
      return 'info' as const;
    case TaskStatus.Approved:
    case TaskStatus.Completed:
      return 'success' as const;
    case TaskStatus.Rejected:
      return 'error' as const;
    case TaskStatus.Cancelled:
      return 'default' as const;
    default:
      return 'default' as const;
  }
}
void statusColor;

/**
 * Task detail page component.
 */
export const TaskDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotificationStore();

  const [task, setTask] = useState<ExternalTaskDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);

  const loadTask = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await hitlService.getTask(id);
      setTask(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
      addNotification('Failed to load task', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadTask, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const handleApproveClick = () => {
    setComments('');
    setApproveDialogOpen(true);
  };

  const handleRejectClick = () => {
    setReason('');
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!task || !id) return;

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Approved,
        responseJson: comments ? JSON.stringify({ comments }) : undefined,
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Task approved successfully', 'success');
      setApproveDialogOpen(false);
      setComments('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to approve task', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!task || !id || !reason.trim()) {
      addNotification('Reason is required for rejection', 'error');
      return;
    }

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Rejected,
        responseJson: JSON.stringify({ reason: reason.trim() }),
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Task rejected successfully', 'success');
      setRejectDialogOpen(false);
      setReason('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to reject task', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!task || !id || !comments.trim()) {
      addNotification('Comments are required for requesting changes', 'error');
      return;
    }

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Pending,
        responseJson: JSON.stringify({ requestedChanges: comments.trim() }),
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Changes requested successfully', 'success');
      setApproveDialogOpen(false);
      setComments('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to request changes', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const canApprove = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;
  const canReject = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;
  const canRequestChanges = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;

  let contextData: any = null;
  if (task?.contextJson) {
    try {
      contextData = JSON.parse(task.contextJson);
    } catch {
      // Invalid JSON, ignore
    }
  }

  let responseData: any = null;
  if (task?.responseJson) {
    try {
      responseData = JSON.parse(task.responseJson);
    } catch {
      // Invalid JSON, ignore
    }
  }

  return (
    <>
      <div className="task-detail" data-testid="task-detail-page">
        <ShellHeader
          title={task?.title || 'Task Details'}
          description="Human-in-the-loop task review."
          actions={
            <>
              <Button variant="outline" size="sm" className="gap-2" onClick={loadTask} disabled={loading} data-testid="refresh-task-btn">
                <RefreshIcon className="h-4 w-4" />
                Refresh
              </Button>
              {canApprove && (
                <Button variant="default" size="sm" className="gap-2" onClick={handleApproveClick} data-testid="approve-task-btn">
                  <ApproveIcon className="h-4 w-4" />
                  Approve
                </Button>
              )}
              {canReject && (
                <Button variant="destructive" size="sm" className="gap-2" onClick={handleRejectClick} data-testid="reject-task-btn">
                  <RejectIcon className="h-4 w-4" />
                  Reject
                </Button>
              )}
            </>
          }
        />

        <PageContainer>
          {loading && <LoadingSkeleton rows={6} data-testid="task-detail-loading" />}
          {error && (
            <InlineErrorPanel
              message={error}
              action={
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                  Dismiss
                </Button>
              }
              data-testid="task-detail-error"
            />
          )}
          {!loading && task && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Main Content */}
            <div className="md:col-span-8">
              <FormSection title="Task Information" data-testid="task-section-info">
                  {task.description && (
                    <>
                      <div className="mb-3">
                        <p className="text-sm font-semibold mb-1">
                          Description
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {task.description}
                        </p>
                      </div>
                      <hr className="border-[var(--color-border-default)] mb-3" />
                    </>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm font-semibold mb-0.5">
                        External Task System
                      </p>
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {task.externalTaskSystemName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold mb-0.5">
                        External Task ID
                      </p>
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {task.externalTaskId}
                      </p>
                    </div>
                    {task.assignedTo && (
                      <div>
                        <p className="text-sm font-semibold mb-0.5">
                          Assigned To
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          User {task.assignedTo.substring(0, 8)}...
                        </p>
                      </div>
                    )}
                    {task.dueDate && (
                      <div>
                        <p className="text-sm font-semibold mb-0.5">
                          Due Date
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {new Date(task.dueDate).toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold mb-0.5">
                        Created At
                      </p>
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {new Date(task.createdAt).toLocaleString()}
                      </p>
                    </div>
                    {task.completedAt && (
                      <div>
                        <p className="text-sm font-semibold mb-0.5">
                          Completed At
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {new Date(task.completedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
              </FormSection>

              {/* Agent Context */}
              {contextData && (
                <FormSection title="Agent Context" data-testid="task-section-context">
                  <div className="p-2 rounded bg-[var(--color-bg-inset)]">
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(contextData, null, 2)}
                    </pre>
                  </div>
                </FormSection>
              )}

              {/* Response */}
              {responseData && (
                <FormSection title="Response" data-testid="task-section-response">
                  <div className="p-2 rounded bg-[var(--color-bg-inset)]">
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(responseData, null, 2)}
                    </pre>
                  </div>
                </FormSection>
              )}
            </div>

            {/* Sidebar */}
            <div className="md:col-span-4">
              <FormSection title="Quick Actions" data-testid="task-section-actions">

                  {task.agentSessionId && (
                    <div className="mb-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/sessions/${task.agentSessionId}`)}
                        data-testid="task-link-session"
                      >
                        View Agent Session
                      </Button>
                    </div>
                  )}

                  {task.safetyGateExecutionId && (
                    <div className="mb-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/safety-gates/executions/${task.safetyGateExecutionId}`)}
                        data-testid="task-link-safety-gate"
                      >
                        View Safety Gate Execution
                      </Button>
                    </div>
                  )}

                  {canRequestChanges && (
                    <div className="mb-2">
                      <Button type="button" variant="outline" size="sm" className="w-full" onClick={handleApproveClick}>
                        Request Changes
                      </Button>
                    </div>
                  )}

                  <hr className="border-[var(--color-border-default)] my-3" />

                  <p className="text-sm font-semibold mb-1">
                    Task History
                  </p>
                  <p className="text-sm text-[var(--color-text-secondary)] mb-1">
                    Created: {new Date(task.createdAt).toLocaleString()}
                  </p>
                  {task.completedAt && (
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      Completed: {new Date(task.completedAt).toLocaleString()}
                    </p>
                  )}
              </FormSection>
            </div>
          </div>
        )}
        </PageContainer>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Task</DialogTitle>
        <DialogContent>
          <Textarea
            className="mt-2"
            rows={4}
            placeholder="Comments (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" onClick={() => setApproveDialogOpen(false)}>Cancel</button>
          <button
            className="rounded-lg border border-amber-500 px-4 py-2 text-sm font-medium text-amber-500 hover:bg-amber-500/10 disabled:opacity-50"
            onClick={handleRequestChanges}
            disabled={updating || !comments.trim()}
          >
            {updating ? 'Processing…' : 'Request Changes'}
          </button>
          <button
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            onClick={handleApprove}
            disabled={updating}
          >
            {updating ? 'Processing…' : 'Approve'}
          </button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Task</DialogTitle>
        <DialogContent>
          <Textarea
            className="mt-2"
            rows={4}
            placeholder="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" onClick={() => setRejectDialogOpen(false)}>Cancel</button>
          <button
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            onClick={handleReject}
            disabled={updating || !reason.trim()}
          >
            {updating ? 'Processing…' : 'Reject'}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};
